import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { SourcingPostingRunningBatch } from 'api-clients/monolith';

import { getRunningPostingBatches } from '@/api-clients/get-running-posting-batches';
import { useFlag } from '@/flags';
import { queryKeys } from '@/query-keys';

export const RUNNING_POSTING_BATCHES_INTERVAL_MS = 30_000;

/**
 * Running bulk posts the user can see, account-wide or for one opening
 * (`openingId` is the opening's external id). Polls every 30 s while any batch
 * runs and stops once none does; window focus refetches either way.
 */
export function useRunningPostingBatches({
  openingId,
}: { openingId?: string } = {}) {
  const enabled = useFlag('hire-source-async-job-posting');

  const { data } = useQuery({
    queryKey: queryKeys.postingBatches.running(openingId),
    queryFn: () => getRunningPostingBatches(openingId),
    enabled,
    refetchInterval: query =>
      query.state.data?.batches.length
        ? RUNNING_POSTING_BATCHES_INTERVAL_MS
        : false,
    refetchOnWindowFocus: 'always',
  });

  // Memoized so consumers get stable references between polls with no change.
  return useMemo(() => {
    const batches: SourcingPostingRunningBatch[] =
      (enabled && data?.batches) || [];
    return {
      batches,
      runningOpeningIds: new Set(batches.flatMap(b => b.funnel_ids)),
    };
  }, [enabled, data]);
}
